/**
 * Constants shared across the OAuth authorization server, the API authorizer and the frontend.
 *
 * Lives in `shared` (rather than `shared_node`) because the SPA needs the login-continuation route,
 * and because keeping the audiences and scopes in one place is what guarantees the API and the
 * gateway can never accidentally agree on the same audience.
 */

// ---------------------------------------------------------------------------
// Audiences — the separation the whole design rests on
// ---------------------------------------------------------------------------

/**
 * Audience of the token the IDE sends to AgentCore Gateway ("token A").
 *
 * Configured as `allowedAudience` on the gateway's CUSTOM_JWT authorizer. The API rejects any token
 * carrying this audience.
 */
export const MCP_GATEWAY_AUDIENCE = 'mavismart-mcp-gateway';

/**
 * Audience of the token AgentCore obtains by token exchange and sends to the API ("token B").
 *
 * The gateway never sees a token with this audience as an inbound credential.
 *
 * Not environment-suffixed: cross-environment replay is already impossible because each environment
 * has its own issuer hostname (checked as `iss`) and its own KMS signing key.
 */
export const API_AUDIENCE = 'mavismart-api';

// ---------------------------------------------------------------------------
// Scopes
// ---------------------------------------------------------------------------

/**
 * Scopes an IDE may request for the gateway token.
 *
 * ONE scope, deliberately. A read/write split was tried and removed, because in this architecture it
 * enforces nothing and therefore misleads:
 *
 *   - dynamic client registration is open, and a client that omits `scope` is granted every scope in
 *     this list, so a client picks its own scopes;
 *   - the gateway advertises every allowed scope, and the gateway target requests a fixed set for every
 *     tool, so any token reaching the API carries all of them;
 *   - there is no consent screen, so the *user* never decides what a client may have.
 *
 * Under those three facts, "read-only client" would be self-declared by the client — worth nothing
 * against anything hostile, while looking like a boundary in the code. A single honest scope is better
 * than two that only appear to restrict.
 *
 * What DOES bound the surface: which routes exist as tools at all (`MCP_ROUTES`) and which are refused
 * outright (`EXCLUDED_FROM_MCP`), both in apps/mavismart_backend/src/api_scopes.ts.
 *
 * Making a read/write split real would take a consent step where the user grants scopes, plus one
 * gateway target per scope. See docs/mcp-agentcore-gateway.md.
 */
export const MCP_SCOPES = ['mcp:api'] as const;
export type McpScope = (typeof MCP_SCOPES)[number];

/** Scopes an API token may carry. */
export const API_SCOPES = ['api'] as const;
export type ApiScope = (typeof API_SCOPES)[number];

/** The single scope every exposed route requires. */
export const API_ACCESS_SCOPE: ApiScope = 'api';

/**
 * Maps each MCP scope to the API scope it grants during token exchange.
 *
 * Exhaustive by type, so adding an entry to `MCP_SCOPES` without deciding what it grants on the API
 * is a compile error. Token exchange may only ever *narrow* through this map — never add a scope the
 * inbound token did not carry.
 */
export const MCP_TO_API_SCOPE: Readonly<Record<McpScope, ApiScope>> = {
  'mcp:api': 'api',
};

export function isMcpScope(scope: string): scope is McpScope {
  return (MCP_SCOPES as readonly string[]).includes(scope);
}

export function isApiScope(scope: string): scope is ApiScope {
  return (API_SCOPES as readonly string[]).includes(scope);
}

/** Parses a space-delimited scope string, tolerating repeated and irregular whitespace. */
export function parseScopeString(scope: string): string[] {
  return scope.split(/\s+/u).filter(s => s.length > 0);
}

/** Serializes scopes into the space-delimited form used in tokens and OAuth parameters. */
export function formatScopeString(scopes: readonly string[]): string {
  return scopes.join(' ');
}

// ---------------------------------------------------------------------------
// Endpoint paths
// ---------------------------------------------------------------------------

export const OAUTH_AUTHORIZE_PATH = '/oauth/authorize';
export const OAUTH_TOKEN_PATH = '/oauth/token';
export const OAUTH_REGISTER_PATH = '/oauth/register';

/**
 * Frontend SPA route that resumes a parked authorization request after the user logs in.
 *
 * Served by the SPA (any unmatched path returns `index.html`), so it must not collide with a backend
 * API route.
 */
export const OAUTH_CONTINUE_PATH = '/oauth/continue';

/** Query parameter carrying the opaque parked-request id through the browser. */
export const OAUTH_REQUEST_ID_PARAM = 'request_id';

/**
 * OIDC discovery document path.
 *
 * Required by AgentCore: `customJWTAuthorizer.discoveryUrl` must end with
 * `.well-known/openid-configuration`.
 */
export const OPENID_CONFIGURATION_PATH = '/.well-known/openid-configuration';

/** RFC 8414 authorization server metadata path, which is what MCP clients look for. */
export const OAUTH_AUTHORIZATION_SERVER_METADATA_PATH = '/.well-known/oauth-authorization-server';

export const JWKS_PATH = '/.well-known/jwks.json';

// ---------------------------------------------------------------------------
// Lifetimes
// ---------------------------------------------------------------------------

/** Authorization codes are single use and must be redeemed immediately (RFC 6749 asks for <= 10 min). */
export const AUTHORIZATION_CODE_TTL_SECONDS = 60;

/** Access token lifetime. Short, because a refresh token avoids sending the user back to the browser. */
export const ACCESS_TOKEN_TTL_SECONDS = 60 * 60;

/** Refresh token lifetime, rotated on every use. */
export const REFRESH_TOKEN_TTL_SECONDS = 30 * 24 * 60 * 60;

/** How long a parked authorization request survives while the user goes through the login flow. */
export const AUTHORIZE_REQUEST_TTL_SECONDS = 10 * 60;

/** Lifetime of the token minted for the API by token exchange. */
export const API_ACCESS_TOKEN_TTL_SECONDS = 15 * 60;
